import { Inject, Injectable, Optional } from '@angular/core';
import { InjectionToken } from '@angular/core';

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

import { AppConstants } from '@unpispas/upp-defs';

import { alertService } from './alert';
import { languageService } from './language';
import { platformService } from './platform';

/**
 * Persist FCM tokens without importing `upp-data` from `upp-base` (layering).
 * Guest provides the store via {@link PUSH_FCM_STORE}.
 */
export interface PushFcmStore {
    CommitToken(token: string, lang: string): Promise<void>;
}

/** Injection token for the FCM persist adapter (`feature/guest`). */
export const PUSH_FCM_STORE = new InjectionToken<PushFcmStore>('PUSH_FCM_STORE');

/**
 * Guest FCM: permission, token, and session token commit via {@link PUSH_FCM_STORE}.
 */
@Injectable({
    providedIn: 'root',
})
export class pushService {
    private _messaging: ReturnType<typeof getMessaging> | null = null;
    private _swregistr: ServiceWorkerRegistration | null = null;

    constructor(
        private lang: languageService,
        private alertCtrl: alertService,
        private platform: platformService,
        @Optional() @Inject(PUSH_FCM_STORE) private store: PushFcmStore | null,
    ) {
        const worker = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
        if (worker?.ready) {
            void worker.ready.then((registration) => {
                this._swregistr = registration;
            });
        }
    }

    Initialize(): void {
        if (getApps().length === 0) {
            initializeApp(AppConstants.FirebaseConfig);
            this._messaging = getMessaging();
            onMessage(this._messaging, (payload) => {
                const title = payload.notification?.title || '';
                console.info('[FIREBASE] (foreground) - (' + title + ')');
            });
            return;
        }

        if (!this._messaging) {
            this._messaging = getMessaging();
        }
    }

    RequiresPermission(): boolean {
        if (!('Notification' in window)) {
            return false;
        }

        switch (Notification.permission) {
            case 'granted':
                return false;
            case 'denied':
                return false;
            default:
                return true;
        }
    }

    RequestPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            return Promise.resolve(false);
        }

        return Notification.requestPermission().then((permission) => permission === 'granted');
    }

    GetToken(): Promise<string | null> {
        if (this.platform.Browser === 'Safari') {
            return Promise.resolve(null);
        }

        this.Initialize();

        if (!this._swregistr || !this._messaging) {
            return Promise.resolve(null);
        }

        return getToken(this._messaging, {
            vapidKey: AppConstants.FCMVapidKey,
            serviceWorkerRegistration: this._swregistr,
        }).then((token) => token || null).catch(() => null);
    }

    /**
     * Obtains an FCM token and commits it via {@link PUSH_FCM_STORE}.
     * @returns `true` when a token was committed; `false` when permission/token failed
     * (guest UI shows the denied overlay).
     */
    async RegisterToken(): Promise<boolean> {
        const token = await this._ObtainToken();
        if (!token) {
            return false;
        }

        if (!this.store) {
            return false;
        }

        await this.store.CommitToken(token, this.lang.GetLanguage(true));
        return true;
    }

    private async _ObtainToken(): Promise<string | null> {
        if (!this.RequiresPermission()) {
            return this.GetToken();
        }

        const confirmed = await this._AskNotification();
        if (!confirmed) {
            return null;
        }

        const granted = await this.RequestPermission();
        if (!granted) {
            return null;
        }

        return this.GetToken();
    }

    private _AskNotification(): Promise<boolean> {
        return new Promise((resolve) => {
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@require_notification_title'),
                message: this.lang.tr('@require_notification_message'),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@ok'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        return false;
                    },
                }],
            });

            alert.onDidDismiss().then((detail: { data?: boolean }) => {
                resolve(detail.data === true);
            });

            void alert.present();
        });
    }
}
